import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Breadcrumbs,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  FormControl,
  Select,
  MenuItem,
  Slider,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Home as HomeIcon,
  BarChart as BarChartIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Build as BuildIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircleOutline,
  Favorite as FavoriteIcon,
  Language as LanguageIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import BackButton from "./BackButton";

// --- Reusable Sub-components ---
const StatCard = ({ title, value, icon, color }) => (
  <Paper
    elevation={3}
    sx={{ p: 2, display: "flex", alignItems: "center", borderRadius: "12px" }}
  >
    <Box
      sx={{
        bgcolor: color,
        borderRadius: "50%",
        p: 1.5,
        mr: 2,
        display: "flex",
        color: "white",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);
const ProgressRing = ({ value, max }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        value={100}
        sx={{ color: "#e0e0e0" }}
        size={60}
        thickness={4}
      />
      <CircularProgress
        variant="determinate"
        value={percentage}
        sx={{ position: "absolute", left: 0 }}
        size={60}
        thickness={4}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
        >{`${Math.round(percentage)}%`}</Typography>
      </Box>
    </Box>
  );
};
const ActivityCard = ({ activity, onAddScore, onViewDetails, hasScoredToday, todayScore }) => (
  <Paper
    elevation={3}
    sx={{ p: 2, borderRadius: "12px", textAlign: "center", height: "100%" }}
  >
    <Typography variant="h6" sx={{ fontWeight: "bold", minHeight: "64px" }}>
      {activity.name}
    </Typography>
    <ProgressRing value={activity.currentScore} max={activity.maxScore} />
    <Typography sx={{ my: 1, fontWeight: "bold", fontSize: "1.2rem" }}>
      {activity.currentScore}/{activity.maxScore}
    </Typography>
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
      {hasScoredToday ? (
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => onAddScore(activity, todayScore)}
          >
            Edit Score: {todayScore}
          </Button>
      ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => onAddScore(activity, null)}
          >
            Add Score
          </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={() => onViewDetails(activity)}
      >
        View Details
      </Button>
    </Box>
  </Paper>
);

// --- Main Parent Therapy View Component ---
const ParentTherapyView = () => {
  const { t, i18n } = useTranslation();
  const { childId, therapyTypeId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [childDetails, setChildDetails] = useState(null);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [therapyDetails, setTherapyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    overallProgress: "0%",
    totalSessions: 0,
    lastScored: "N/A",
  });
  const [activities, setActivities] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [comparisonChartData, setComparisonChartData] = useState([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [newScore, setNewScore] = useState("");
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = async () => {
    setLoading(true);
    try {
      const childRes = await axios.get(
        `${API_BASE_URL}/get_children.php?child_id=${childId}`
      );
      setChildDetails(childRes.data[0]);

      if (!therapyTypeId) {
        const therapyTypesRes = await axios.get(
          `${API_BASE_URL}/get_therapy_types.php`
        );
        setTherapyTypes(therapyTypesRes.data);
      } else {
        const typesRes = await axios.get(
          `${API_BASE_URL}/get_therapy_types.php`
        );
        const currentTherapy = typesRes.data.find(
          (t) => t.id === Number(therapyTypeId)
        );
        setTherapyDetails(currentTherapy);

        const activitiesRes = await axios.get(
          `${API_BASE_URL}/get_activities.php?therapy_type_id=${therapyTypeId}`
        );
        const allActivities = activitiesRes.data.map((act) => ({
          ...act,
          currentScore: 0,
          maxScore: 10,
          history: [],
        }));

        const progressRes = await axios.get(
          `${API_BASE_URL}/get_therapy_progress.php?child_id=${childId}&therapy_type_id=${therapyTypeId}`
        );
        const progressData = progressRes.data;

        const dedupedProgressMap = {};
        progressData.forEach((item) => {
            const key = `${item.session_date}_${item.activity_id}_${item.scorer_type}`;
            dedupedProgressMap[key] = item;
        });
        const cleanProgressData = Object.values(dedupedProgressMap).sort((a,b) => new Date(a.session_date) - new Date(b.session_date));

        const doctorScoredActivityIds = new Set();
        cleanProgressData.forEach((item) => {
          if (item.scorer_type === "doctor") {
            doctorScoredActivityIds.add(Number(item.activity_id));
          }
        });

        const activityMap = {};
        allActivities.forEach((act) => (activityMap[act.id] = { ...act }));
        cleanProgressData.forEach((item) => {
          if (activityMap[item.activity_id]) {
            activityMap[item.activity_id].history.push(item);
            activityMap[item.activity_id].currentScore = item.score;
          }
        });

        const visibleActivities = allActivities
          .filter((act) => doctorScoredActivityIds.has(act.id))
          .map((act) => activityMap[act.id]);

        setActivities(visibleActivities);

        const parentDates = {};
        const comparisonDates = {};
        cleanProgressData.forEach((item) => {
          const activityName = activityMap[item.activity_id]?.name;
          if (!activityName) return;

          // Build comparison chart data
          if (!comparisonDates[item.session_date]) {
            comparisonDates[item.session_date] = { date: item.session_date };
          }
          const isParent = item.scorer_type === "parent";
          const keyName = `${activityName} (${isParent ? "Parent" : "Doc"})`;
          comparisonDates[item.session_date][keyName] = item.score;

          // Build parent strictly chart data
          if (isParent) {
            if (!parentDates[item.session_date]) {
              parentDates[item.session_date] = { date: item.session_date };
            }
            parentDates[item.session_date][activityName] = item.score;
          }
        });

        setChartData(Object.values(parentDates).sort((a,b) => new Date(a.date) - new Date(b.date)));
        setComparisonChartData(Object.values(comparisonDates).sort((a,b) => new Date(a.date) - new Date(b.date)));

        const uniqueSessions = new Set(cleanProgressData.map((p) => p.session_date));
        const totalScores = visibleActivities.reduce(
          (sum, act) => sum + act.currentScore,
          0
        );
        const totalMax = visibleActivities.reduce(
          (sum, act) => sum + act.maxScore,
          0
        );
        const overallProgress =
          totalMax > 0 ? Math.round((totalScores / totalMax) * 100) : 0;
        const parentScores = cleanProgressData
          .filter((p) => p.scorer_type === "parent")
          .sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
        setStats({
          overallProgress: `${overallProgress}%`,
          totalSessions: uniqueSessions.size,
          lastScored:
            parentScores.length > 0
              ? new Date(parentScores[0].session_date).toLocaleDateString()
              : "N/A",
        });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, therapyTypeId]);

  const handleAddScoreClick = (activity, existingScore) => {
    setSelectedActivity(activity);
    if (existingScore !== null && existingScore !== undefined) {
      setNewScore(existingScore);
      setIsEditingScore(true);
    } else {
      setNewScore("");
      setIsEditingScore(false);
    }
    setScoreModalOpen(true);
  };
  const handleViewDetailsClick = (activity) => {
    setSelectedActivity(activity);
    setDetailsModalOpen(true);
  };
  const handleModalClose = () => {
    setScoreModalOpen(false);
    setDetailsModalOpen(false);
    setSelectedActivity(null);
    setNewScore("");
  };
  const handleScoreSubmit = async () => {
    if (!selectedActivity || newScore === "") return;
    try {
      await axios.post(`${API_BASE_URL}/manage_therapy_session.php`, {
        child_id: childId,
        therapy_type_id: therapyTypeId,
        session_date: sessionDate,
        scorer_type: "parent",
        activities: activities.map((act) => ({
          activity_id: act.id,
          score: act.id === selectedActivity.id ? newScore : act.currentScore,
          notes: act.id === selectedActivity.id ? "Scored by parent" : "Carried over from previous session",
        })),
      });
      fetchData();
    } catch (error) {
      console.error("Failed to submit score:", error);
    }
    handleModalClose();
  };
  const handleLogout = () => {
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    navigate("/login");
  };
  const changeLanguage = (event) => {
    i18n.changeLanguage(event.target.value);
  };
  const getTherapyIcon = (typeName) => {
    switch (typeName.toLowerCase()) {
      case "behavioral":
        return <BarChartIcon sx={{ fontSize: 40, color: "#2196f3" }} />;
      case "speech":
        return <RecordVoiceOverIcon sx={{ fontSize: 40, color: "#9c27b0" }} />;
      case "occupational":
        return <BuildIcon sx={{ fontSize: 40, color: "#4caf50" }} />;
      default:
        return <BarChartIcon sx={{ fontSize: 40, color: "#2196f3" }} />;
    }
  };
  const getTherapyDescription = (typeName) => {
    switch (typeName.toLowerCase()) {
      case "behavioral":
        return "Focus on modifying behaviors through positive reinforcement.";
      case "speech":
        return "Improve communication skills and language development.";
      case "occupational":
        return "Enhance daily living skills and motor development.";
      default:
        return "Comprehensive therapy for child development.";
    }
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!therapyTypeId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <AppBar
          position="static"
          color="default"
          elevation={1}
          sx={{ bgcolor: "white" }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  bgcolor: "#00bcd4",
                  borderRadius: "8px",
                  p: 0.5,
                  display: "flex",
                }}
              >
                <FavoriteIcon sx={{ color: "white", fontSize: "20px" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {t("akidscare_brand")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LanguageIcon sx={{ color: "text.secondary" }} />
                <FormControl variant="standard" sx={{ minWidth: 60 }}>
                  <Select
                    value={i18n.language}
                    onChange={changeLanguage}
                    disableUnderline
                  >
                    <MenuItem value="en">EN</MenuItem>
                    <MenuItem value="te">TE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                {t("logout_button")}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Container
          maxWidth="md"
          sx={{
            py: 8,
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
        <Box sx={{ width: "100%", maxWidth: 800 }}>
          <BackButton />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper
            elevation={3}
            sx={{ bgcolor: "white", p: 4, borderRadius: "16px", width: "100%", maxWidth: 600 }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
            >
              Select Analysis Type
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ textAlign: "center", mb: 4, color: "text.secondary" }}
            >
              Choose a therapy to review for {childDetails?.full_name}.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {therapyTypes.map((type) => (
                <Paper
                  component={Link}
                  to={`/parent-therapy/${childId}/${type.id}`}
                  key={type.id}
                  elevation={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 3,
                    borderRadius: "12px",
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#f5f5f5",
                      borderColor: "#00bcd4",
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box sx={{ mr: 3 }}>{getTherapyIcon(type.name)}</Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      {type.name} Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getTherapyDescription(type.name)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
          </Box>
        </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: "#f4f6f8",
      minHeight: "100vh",
      py: 6,
    }}>
      <style>{`@media print { body * { visibility: hidden; } #report-content, #report-content * { visibility: visible; } #report-content { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none; } .MuiPaper-root { box-shadow: none !important; border: 1px solid #ddd; } }`}</style>
      <Container maxWidth="xl">
        <BackButton className="no-print" sx={{ display: { xs: 'flex' }, mb: 4 }} />
        <Box id="report-content" ref={reportRef}>
          <Box sx={{ mb: 4 }} className="no-print">
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component={RouterLink}
              to={`/parent-therapy/${childId}`}
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} />
              Select Type
            </Link>
            <Typography color="text.primary">
              {childDetails?.full_name}'s Progress
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
            {therapyDetails?.name} Progress for {childDetails?.full_name}
          </Typography>
        </Box>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* <Grid item xs={12} sm={4}>
            <StatCard
              title="Overall Progress"
              value={stats.overallProgress}
              icon={<BarChartIcon />}
              color="#29b6f6"
            />
          </Grid> */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Sessions Logged"
              value={stats.totalSessions}
              icon={<CalendarTodayIcon />}
              color="#66bb6a"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="You Last Scored"
              value={stats.lastScored}
              icon={<CheckCircleOutline />}
              color="#ffa726"
            />
          </Grid>
        </Grid>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: "12px", mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
            Activities
          </Typography>
          <Grid container spacing={3}>
            {activities.map((activity) => {
              const todayIso = new Date().toISOString().slice(0, 10);
              const todayEntry = activity.history.find(h => h.session_date === todayIso && h.scorer_type === "parent");
              return (
                <Grid item xs={12} sm={6} md={4} key={activity.id}>
                  <ActivityCard
                    activity={activity}
                    hasScoredToday={!!todayEntry}
                    todayScore={todayEntry ? todayEntry.score : null}
                    onAddScore={handleAddScoreClick}
                    onViewDetails={handleViewDetailsClick}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Paper>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: "12px", mt: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
            My Therapy Progress
          </Typography>
          <Box sx={{ height: isMobile ? 300 : 400, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: isMobile ? 20 : 30, left: isMobile ? 0 : 0, bottom: isMobile ? 20 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} 
                  axisLine={false} 
                  tickLine={false}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  interval={isMobile ? "preserveStartEnd" : 0}
                />
                <YAxis tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} axisLine={false} tickLine={false} />
                <Tooltip 
                  isAnimationActive={false}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} 
                />
                <Legend 
                  iconType="circle" 
                  wrapperStyle={{ 
                    paddingTop: isMobile ? '30px' : '20px',
                    fontSize: isMobile ? '10px' : '12px'
                  }} 
                  verticalAlign="bottom"
                  align="center"
                />
                {activities.slice(0, 5).map((act, index) => (
                  <Line
                    key={act.id}
                    type="monotone"
                    dataKey={act.name}
                    stroke={["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#413ea0"][index % 5]}
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: isMobile ? 8 : 6 }}
                    connectNulls={true}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: "12px" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
            Parent vs Doctor Comparison
          </Typography>
          <Box sx={{ height: isMobile ? 350 : 400, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={comparisonChartData} margin={{ top: 10, right: isMobile ? 20 : 30, left: isMobile ? 0 : 0, bottom: isMobile ? 30 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} 
                  axisLine={false} 
                  tickLine={false}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  interval={isMobile ? "preserveStartEnd" : 0}
                />
                <YAxis tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} axisLine={false} tickLine={false} />
                <Tooltip 
                  isAnimationActive={false}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} 
                />
                <Legend 
                  iconType="circle" 
                  wrapperStyle={{ 
                    paddingTop: isMobile ? '40px' : '20px',
                    fontSize: isMobile ? '9px' : '11px'
                  }} 
                  verticalAlign="bottom"
                  align="center"
                />
                {activities.slice(0, 5).map((act, index) => {
                  const color = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#413ea0"][index % 5];
                  return (
                    <React.Fragment key={act.id}>
                      <Line type="monotone" dataKey={`${act.name} (Doc)`} stroke={color} strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: isMobile ? 8 : 6 }} connectNulls={true} />
                      <Line type="monotone" dataKey={`${act.name} (Parent)`} stroke={color} strokeWidth={3} strokeDasharray="5 5" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: isMobile ? 8 : 6 }} connectNulls={true} />
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
        </Box>
      </Container>
      <Dialog open={scoreModalOpen} onClose={handleModalClose}>
        <DialogTitle>{isEditingScore ? "Edit Your Score for" : "Add Your Score for"} {selectedActivity?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, px: 2, minWidth: 300, textAlign: 'center' }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
              {newScore || 0}
            </Typography>
            <TextField
              type="number"
              inputProps={{ min: 0, max: 10, step: "0.1" }}
              value={newScore}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (Number(val) >= 0 && Number(val) <= 10)) {
                  setNewScore(val);
                }
              }}
              fullWidth
              variant="outlined"
              placeholder="Enter score (0-10, decimals allowed)"
              autoFocus
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Enter a score from 0 to 10. Decimals are allowed.
            </Typography>
            <TextField
              type="date"
              label="Session Date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button onClick={handleScoreSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={detailsModalOpen}
        onClose={handleModalClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Score History for {selectedActivity?.name}</DialogTitle>
        <DialogContent>
          <List>
            {selectedActivity?.history
              .map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {`Score: ${item.score}`}{" "}
                          <Chip
                            label={item.scorer_type}
                            size="small"
                            color={
                              item.scorer_type === "doctor"
                                ? "primary"
                                : "success"
                            }
                            sx={{ ml: 1 }}
                          />{" "}
                        </>
                      }
                      secondary={new Date(
                        item.session_date
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
              .reverse()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
const RouterLink = Link;
export default ParentTherapyView;
